/**
 * Eric 2026-06-25 (iter123 — Session A5) — Panneau Scripts de vente.
 * Extrait de VideoDetailPage.js (refacto P0).
 *
 * Affiche bannière statut + sous-onglets (Pipeline / Visualiser / Modifier)
 * gated par `canEdit` (Vincent #336). Le panneau Pipeline lance la
 * génération via la mise sous-onglet "edit" qui rend <ScriptsPage embedded>.
 */
import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { confirmDialog } from "../../lib/uiDialogs";
import ScriptsPage from "../../pages/ScriptsPage";
// Enhancement B614.d (Eric 2026-07-30) — Set des KUs favorites pour afficher
// un compteur « ♥ N favoris » dans la bannière statut (parité Q&R/Procédures).
import { useFavoriteKuIds } from "../../hooks/useFavoriteKuIds";

export const ScriptsPanel = ({
  show,
  canEdit,
  isAdmin,
  lang,
  api,
  videoId,
  knowledgeUnits,
  scriptsCount,
  scriptsList,
  allTranscripts,
  pipelineHasEverRun,
  scriptsSubtab,
  setScriptsSubtab,
  scriptsKeepPrevious,
  setScriptsKeepPrevious,
  importDocxLoading,
  importDocxFile,
  loadVideoData,
  openReportModal,
  seekTo,
  onScriptCardClick,
  onScriptsListChange,
  scriptsReloadTick = 0,
}) => {
  const favKuIds = useFavoriteKuIds();
  // Vincent B794 v2 (Eric 2026-02-12) — Plein écran SUPPRIMÉ à la
  // demande de Vincent : « le mode plein écran pour les scripts ne
  // devrait pas exister ». Retour aux 3 sous-onglets inline
  // Pipeline / Visualiser / Modifier, sans overlay.

  // Vincent #433 (Eric 2026-07-07 SPEC STRICK) — La DB stocke le type sous
  // 3 variantes historiques : "script", "sales_script", "sales-script".
  // Le comptage doit couvrir les 3 sinon la bulle est incorrecte.
  const isScriptType = (t) => t === "script" || t === "sales_script" || t === "sales-script";
  // Vincent B813 (Eric 2026-02-13) — Même bug racine que B804 : les scripts
  // vivent dans `sales_scripts` (pas `knowledge_units`) → le filtre KU
  // retournait 0 même quand un script existait. Résultat : Vincent voyait
  // le script apparaître 2s (state React local) puis « disparaître » au
  // refetch (scriptsTotal=0 → UI reprend le layout « aucun script »).
  //
  // Priorité pour le compteur :
  //   1) `scriptsList.length` (temps réel, publié par ScriptsPage embedded)
  //   2) `scriptsCount` (source de vérité serveur via GET /api/videos/:id)
  //   3) fallback KU (rétro-compat, toujours 0 en pratique)
  const scriptCountFromKU = (knowledgeUnits || []).filter((k) => isScriptType(k.type)).length;
  const scriptsTotal =
    Array.isArray(scriptsList) && scriptsList.length > 0
      ? scriptsList.length
      : (typeof scriptsCount === "number" ? scriptsCount : scriptCountFromKU);
  const hasRun = scriptsTotal > 0;

  // Tâche 1446 (Eric 2026-06) — ⛔ **NE JAMAIS REMETTRE LE RENVOI AUTOMATIQUE
  // VERS « Liste des Scripts » ICI.**
  //
  // Un `useEffect` posé en B794 ramenait sur « Liste des Scripts » dès que
  // `scriptsSubtab === "pipeline"` et que des scripts existaient. Son intention
  // (« arriver sur les scripts existants, pas sur le formulaire ») visait
  // l'ARRIVÉE sur le panneau — mais un effet ne distingue pas l'arrivée d'un
  // clic : il se redéclenchait à CHAQUE clic sur « Pipeline », si bien que
  // l'onglet était **inaccessible** dès qu'une vidéo avait un seul script
  // (reproduit sur « 01.Le Start up » : le soulignement ne bougeait pas et la
  // vue ne s'affichait jamais).
  // Combiné au masquage du raccourci de la bannière (2026-07), il ne restait
  // **AUCUNE porte** vers « Générer à nouveau » — c'est-à-dire la panne
  // complète signalée par Eric.
  // L'intention de B794 est de toute façon déjà satisfaite SANS effet : le
  // sous-onglet par défaut est « edit » depuis B869
  // (`VideoDetailPage.js` : `useState("edit")`). L'effet ne faisait donc plus
  // qu'une seule chose : empêcher un clic volontaire.

  if (!show) return null;

  // Enhancement B614.d (Eric 2026-07-30) — Compteur ♥ favoris.
  const scriptsFavorites = (knowledgeUnits || []).filter((k) => isScriptType(k.type) && favKuIds.has(k.id)).length;
  const lastScriptAt = Math.max(
    0,
    ...(knowledgeUnits || [])
      .filter((k) => isScriptType(k.type))
      .map((k) => new Date(k.created_at || 0).getTime())
  );
  const mergedUpdatedAt = new Date(
    allTranscripts?.merged?.updated_at || allTranscripts?.merged?.created_at || 0
  ).getTime();
  const transcriptModifiedAfterRun = hasRun && mergedUpdatedAt > 0 && mergedUpdatedAt > lastScriptAt;

  const handleLaunch = async () => {
    if (hasRun && scriptsTotal > 0 && !scriptsKeepPrevious) {
      const ok = await confirmDialog(
        lang === "fr"
          ? `Les ${scriptsTotal} scripts de vente actuels seront SUPPRIMÉS avant relance. Continuer ?`
          : `The ${scriptsTotal} current sales scripts will be DELETED before relaunch. Continue?`,
        { tone: "warning" }
      );
      if (!ok) return;
      try {
        await axios.delete(`${api}/api/agent/knowledge-units/by-video/${videoId}?type=script`, {
          withCredentials: true,
        });
        toast.info(
          lang === "fr" ? `${scriptsTotal} scripts supprimés.` : `${scriptsTotal} scripts deleted.`
        );
        loadVideoData();
      } catch (e) {
        toast.error(`Erreur suppression : ${e.response?.data?.detail || e.message}`);
        return;
      }
    }
    setScriptsSubtab("edit");
  };

  return (
    <div className="flex flex-col" data-testid="scripts-panel">
      {/* Vincent bug #84 (2026-05-08) — Bannière de statut. */}
      <div
        className="px-4 py-2.5 bg-amber-50/50 border-b border-amber-200 flex items-center justify-between gap-3 text-[11px]"
        data-testid="scripts-status-banner"
      >
        <div className="flex items-center gap-3">
          <span className="font-bold text-amber-800">
            {scriptsTotal} {lang === "fr" ? "script(s) de vente" : "sales script(s)"}
          </span>
          {scriptsFavorites > 0 && (
            <span className="text-rose-700" data-testid="scripts-favorites-count">
              ♥ {scriptsFavorites} {lang === "fr" ? (scriptsFavorites > 1 ? "favoris" : "favori") : (scriptsFavorites > 1 ? "favorites" : "favorite")}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {scriptsTotal === 0 && !pipelineHasEverRun && (
            <span className="text-amber-700/80 italic">
              {lang === "fr" ? "Pipeline non lancé" : "Pipeline not run"}
            </span>
          )}
          {/* Vincent XLSX 2026-07-15 Row 486 (Eric 2026-07-16 SPEC STRICK) —
              Le bouton n'apparaissait QUE si le pipeline n'avait jamais
              tourné, le relais étant « Générer à nouveau » dans la
              pipeline-box.
              ⚠️ Tâche 1446 (Eric 2026-06) — **RACCOURCI RÉTABLI SUR SA
              DEMANDE EXPRESSE** (« oui, remets le raccourci dans la bannière
              en plus »). Le relais supposé se trouve DANS la vue Pipeline :
              quand l'onglet Pipeline est devenu inaccessible, ce masquage a
              transformé un défaut d'onglet en panne totale. Deux portes
              valent mieux qu'une porte unique qui peut se fermer. */}
          {canEdit && (
            <button
              onClick={() => setScriptsSubtab("pipeline")}
              className="px-2 py-1 rounded text-[10px] font-bold bg-amber-600 hover:bg-amber-700 text-white transition-colors"
              data-testid="scripts-banner-pipeline-shortcut"
            >
              ⚗️ {scriptsTotal === 0 ? (lang === "fr" ? "Lancer" : "Run") : lang === "fr" ? "Relancer" : "Re-run"}
            </button>
          )}
        </div>
      </div>

      {/* Vincent B794 (Eric 2026-02-12) — Restauration du pattern à 3
          sous-onglets Pipeline / Visualiser / Modifier, conforme à Q&R et
          Procédures. Le plein écran devient un bouton OPTIONNEL, pas la
          seule voie d'affichage. Consigne Vincent : « il faut les remettre
          de façon conforme » à la base de connaissance. */}
      {/* Vincent B839 (Eric 2026-02-14 SPEC STRICK PROD) —
          Exactement 2 sous-onglets pour TOUS :
            - Pipeline (visible pour tous ; boutons gated isAdmin)
            - Modifier / Visualiser (label selon canEdit)
          Vincent B848 (Eric 2026-02-14 SPEC STRICK PROD) — Bouton
          Pipeline masqué pour non-éditeurs (parité 5 panneaux). */}
      <div className="flex border-b border-zinc-200 bg-zinc-50">
        {canEdit && (
          <button
            onClick={() => setScriptsSubtab("pipeline")}
            className={`flex-1 py-2 text-xs font-medium transition-colors relative ${
              scriptsSubtab === "pipeline"
                ? "text-amber-700 bg-white"
                : "text-zinc-500 hover:text-zinc-700"
            }`}
            data-testid="scripts-subtab-pipeline"
          >
            ⚗️ {lang === "fr" ? "Pipeline" : "Pipeline"}
            {scriptsSubtab === "pipeline" && (
              <div className="absolute bottom-0 left-2 right-2 h-0.5 bg-amber-500 rounded-full" />
            )}
          </button>
        )}
        <button
          onClick={() => setScriptsSubtab(canEdit ? "edit" : "view")}
          className={`flex-1 py-2 text-xs font-medium transition-colors relative ${
            (scriptsSubtab === "edit" || scriptsSubtab === "view")
              ? "text-amber-700 bg-white"
              : "text-zinc-500 hover:text-zinc-700"
          }`}
          data-testid={canEdit ? "scripts-subtab-edit" : "scripts-subtab-view"}
        >
          {canEdit
            ? <>📋 {lang === "fr" ? "Liste des Scripts" : "Scripts list"}</>
            : <>👁 {lang === "fr" ? "Visualiser" : "View"}</>}
          {(scriptsSubtab === "edit" || scriptsSubtab === "view") && (
            <div className="absolute bottom-0 left-2 right-2 h-0.5 bg-amber-500 rounded-full" />
          )}
        </button>
      </div>

      <div className="p-4 space-y-3">
        {(scriptsSubtab === "pipeline" || scriptsSubtab === "generate") && (
          <div className="space-y-3" data-testid="scripts-pipeline-view">
            <p className="text-xs text-zinc-600">
              {lang === "fr"
                ? "Pipeline IA dédié extrayant les scripts de vente conversationnels (objections, transitions, fermeture). Durée : 2-5 min."
                : "Dedicated AI pipeline extracting sales scripts. Duration: 2-5 min."}
            </p>
            {transcriptModifiedAfterRun && (
              <div
                className="px-3 py-2 rounded border border-amber-300 bg-amber-50 text-[11px] text-amber-900 flex items-start gap-2"
                data-testid="scripts-transcript-modified-warning"
              >
                <span>⚠️</span>
                <span>
                  {lang === "fr"
                    ? "La transcription fusionnée a été modifiée depuis la dernière exécution du pipeline. Cliquez « Générer à nouveau » pour mettre à jour les scripts."
                    : "The merged transcript has been modified since the last pipeline run. Click 'Generate again' to refresh scripts."}
                </span>
              </div>
            )}
            {isAdmin && (
              <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-3" data-testid="scripts-pipeline-box">
                {/* Vincent #21 batch FLHQ — Checkbox "Conserver les scripts". */}
                {hasRun && scriptsTotal > 0 && (
                  <label
                    className="flex items-center gap-2 text-xs text-zinc-700 mb-2"
                    data-testid="scripts-keep-previous-label"
                  >
                    <input
                      type="checkbox"
                      checked={scriptsKeepPrevious}
                      onChange={(e) => setScriptsKeepPrevious(e.target.checked)}
                      data-testid="scripts-keep-previous-checkbox"
                      className="rounded"
                    />
                    {lang === "fr"
                      ? `Conserver les ${scriptsTotal} scripts de vente déjà générés (sinon ils seront supprimés avant relance)`
                      : `Keep existing ${scriptsTotal} sales scripts (otherwise delete before relaunch)`}
                  </label>
                )}
                {/* Vincent B839 (Eric 2026-02-14 SPEC STRICK PROD) —
                    Grille 2×2 EXACTE :
                       [ Générer/Régénérer | Effacer ]
                       [ Rapport           | Importer ] */}
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={handleLaunch}
                    className="px-3 py-2.5 rounded-lg border-2 border-amber-300 bg-white hover:bg-amber-100 text-amber-800 transition-all text-xs font-medium leading-tight"
                    data-testid="scripts-pipeline-launch-btn"
                  >
                    ⚗️{" "}
                    {lang === "fr"
                      ? hasRun
                        ? "Générer à nouveau les scripts de vente"
                        : "Générer les scripts de vente"
                      : hasRun
                      ? "Generate scripts again"
                      : "Generate scripts"}
                  </button>
                  <button
                    onClick={async () => {
                      // Vincent B648 v2 (Eric 2026-08-01 SPEC STRICK PROD) —
                      // Bouton dédié pour effacer les scripts, indépendant du
                      // toggle `scriptsKeepPrevious`.
                      if (!scriptsTotal) {
                        toast.info(lang === "fr" ? "Aucun script à effacer" : "No script to clear");
                        return;
                      }
                      const ok = await confirmDialog(lang === "fr"
                        ? `Effacer définitivement les ${scriptsTotal} script(s) de vente ? Cette action est irréversible.`
                        : `Permanently delete the ${scriptsTotal} sales script(s)? This cannot be undone.`);
                      if (!ok) return;
                      try {
                        const r = await axios.delete(`${api}/api/agent/knowledge-units/by-video/${videoId}?type=script`, { withCredentials: true });
                        toast.success(lang === "fr"
                          ? `${r?.data?.count || scriptsTotal} script(s) effacé(s)`
                          : `${r?.data?.count || scriptsTotal} script(s) cleared`);
                        loadVideoData && loadVideoData();
                      } catch (e) {
                        toast.error(e.response?.data?.detail || "Erreur suppression scripts");
                      }
                    }}
                    className="px-3 py-2.5 rounded-lg border-2 border-red-300 bg-white hover:bg-red-100 text-red-800 transition-all text-xs font-medium leading-tight"
                    data-testid="clear-scripts-btn"
                    title={lang === "fr" ? "Effacer tous les scripts de vente de cette vidéo" : "Clear all sales scripts for this video"}
                  >
                    🗑 {lang === "fr" ? "Effacer les scripts" : "Clear scripts"}
                  </button>
                  <button
                    onClick={() => openReportModal("scripts")}
                    disabled={!hasRun}
                    className="px-3 py-2.5 rounded-lg border-2 border-blue-300 bg-white hover:bg-blue-100 text-blue-800 transition-all text-xs font-medium leading-tight disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="scripts-report-btn"
                  >
                    📄 {lang === "fr" ? "Rapport" : "Report"}
                  </button>
                  {/* Vincent #254 — Import .docx Scripts (B839 : intégré dans la grille 2×2) */}
                  <label className="block">
                    <input
                      type="file"
                      accept=".docx"
                      className="hidden"
                      onChange={(e) => importDocxFile(e.target.files?.[0], "sales-script")}
                      data-testid="scripts-import-docx-input"
                    />
                    <span
                      className={`block w-full text-center px-3 py-2.5 rounded-lg border-2 border-[#002FA7] bg-white hover:bg-blue-50 text-[#002FA7] transition-all text-xs font-medium leading-tight cursor-pointer ${
                        importDocxLoading ? "opacity-50 cursor-wait" : ""
                      }`}
                      data-testid="scripts-import-docx-btn"
                    >
                      {importDocxLoading
                        ? lang === "fr"
                          ? "⏳ Import en cours…"
                          : "⏳ Importing…"
                        : (
                          <>
                            📥{" "}
                            {lang === "fr"
                              ? "Importer Scripts (fichier Word .docx)"
                              : "Import Scripts (Word .docx file)"}
                          </>
                        )}
                    </span>
                  </label>
                </div>
              </div>
            )}
          </div>
        )}
        {/* Vincent B794 v2 (Eric 2026-02-12) — Affichage EMBARQUÉ inline
            SANS option plein écran. Vincent : « le mode plein écran
            pour les scripts ne devrait pas exister ; on devrait
            seulement pouvoir les voir dans la largeur de la page
            disponible, sans que les scripts passent derrière le menu
            de gauche. » */}
        {(scriptsSubtab === "view" || scriptsSubtab === "edit") && (
          <div className="space-y-3" data-testid={`scripts-${scriptsSubtab}-view`}>
            <ScriptsPage
              embedded
              readOnly={scriptsSubtab === "view"}
              onSeek={seekTo}
              onCardClick={onScriptCardClick}
              onScriptsListChange={onScriptsListChange}
              reloadTick={scriptsReloadTick}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ScriptsPanel;
